// CoinGecko Coin-ID-Resolver für Long-Tail-Coins.
//
// Problem: COIN_MAP (coinList.js) hat ~250 hardcoded Coins. Wer einen
// Coin außerhalb dieser Liste hält (z.B. einen neuen ERC-20), bekommt
// weder Live-Preis noch Logo — CoinImage fällt auf die Letter-Pille.
//
// Lösung: Symbol → ID dynamisch via CoinGecko /search (public, kein Key
// nötig, CORS-enabled). Persistent gecacht in localStorage. COIN_MAP wird
// zur Laufzeit mutiert, damit alle bestehenden `COIN_MAP[sym]?.id`-Aufrufe
// auch die resolved Coins sehen.
//
// Negative-Cache (7d) verhindert wiederholte 404-Lookups für Tippfehler
// oder unbekannte Coins. Positive-Cache 30d.

import { COIN_MAP } from './coinList';

const POSITIVE_TTL = 30 * 24 * 60 * 60 * 1000;
const NEGATIVE_TTL =  7 * 24 * 60 * 60 * 1000;
const KEY_PREFIX = 'redapp_cg_resolved_v1__';

const memCache = new Map();   // SYM -> { id, name } | null
const inflight = new Map();   // SYM -> Promise

// Eager-Hydrate: persistente Resolved-Coins schon beim Modul-Load in COIN_MAP
// kippen, damit der allererste Render keine Lücken hat. Wird sonst erst beim
// nächsten resolveCoin()-Aufruf gemacht — dann ist die Hero-Card schon einmal
// kurz "leer" gerendert.
(function hydrateFromStorage() {
  if (typeof localStorage === 'undefined') return;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(KEY_PREFIX)) continue;
      const SYM = key.slice(KEY_PREFIX.length);
      if (COIN_MAP[SYM]?.id) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const obj = JSON.parse(raw);
      if (!obj || typeof obj.ts !== 'number') continue;
      const ttl = obj.id ? POSITIVE_TTL : NEGATIVE_TTL;
      if (Date.now() - obj.ts > ttl) continue;
      if (!obj.id) { memCache.set(SYM, null); continue; }
      const val = { id: obj.id, name: obj.name || SYM };
      memCache.set(SYM, val);
      COIN_MAP[SYM] = { sym: SYM, id: val.id, name: val.name, color: '#888', bg: '#1a1a22', _resolved: true };
    }
  } catch { /* localStorage corrupt — best effort */ }
})();

function readCache(SYM) {
  if (memCache.has(SYM)) return memCache.get(SYM);
  try {
    const raw = localStorage.getItem(KEY_PREFIX + SYM);
    if (!raw) return undefined;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj.ts !== 'number') return undefined;
    const ttl = obj.id ? POSITIVE_TTL : NEGATIVE_TTL;
    if (Date.now() - obj.ts > ttl) return undefined;
    const val = obj.id ? { id: obj.id, name: obj.name || SYM } : null;
    memCache.set(SYM, val);
    return val;
  } catch { return undefined; }
}

function writeCache(SYM, value) {
  memCache.set(SYM, value);
  try {
    localStorage.setItem(
      KEY_PREFIX + SYM,
      JSON.stringify(value ? { id: value.id, name: value.name, ts: Date.now() } : { id: null, ts: Date.now() })
    );
  } catch {}
}

async function searchCoinGecko(SYM) {
  const url = `https://api.coingecko.com/api/v3/search?query=${encodeURIComponent(SYM)}`;
  try {
    const res = await fetch(url, { headers: { accept: 'application/json' } });
    if (!res.ok) return null;
    const data = await res.json();
    const coins = Array.isArray(data?.coins) ? data.coins : [];
    if (!coins.length) return null;
    // Bevorzugt exakter Symbol-Match. Bei mehreren: höchster Market-Cap-Rank
    // gewinnt (kleinere Zahl = besser). CG sortiert ohnehin schon grob nach
    // Relevanz, aber Symbol-Tie-Breaker schützt vor Long-Tail-Müll-Coins.
    const exact = coins.filter(c => (c.symbol || '').toUpperCase() === SYM);
    if (exact.length) {
      exact.sort((a, b) => (a.market_cap_rank ?? 1e9) - (b.market_cap_rank ?? 1e9));
      const c = exact[0];
      return { id: c.id, name: c.name || SYM };
    }
    const c = coins[0];
    return { id: c.id, name: c.name || SYM };
  } catch { return null; }
}

/**
 * Resolve coin id for a symbol. Mutates COIN_MAP zur Laufzeit, damit
 * downstream-Konsumenten (CoinImage, Reports, etc.) den resolved Coin sehen.
 * Returns { id, name } oder null.
 */
export async function resolveCoin(sym) {
  if (!sym) return null;
  const SYM = String(sym).toUpperCase();

  // Static map first
  const stat = COIN_MAP[SYM];
  if (stat?.id) return { id: stat.id, name: stat.name || SYM };

  // Persistente / Memory-Cache
  const cached = readCache(SYM);
  if (cached !== undefined) {
    if (cached?.id && !COIN_MAP[SYM]) {
      COIN_MAP[SYM] = { sym: SYM, id: cached.id, name: cached.name || SYM, color: '#888', bg: '#1a1a22', _resolved: true };
    }
    return cached;
  }

  if (inflight.has(SYM)) return inflight.get(SYM);

  const p = (async () => {
    const hit = await searchCoinGecko(SYM);
    const value = hit ? { id: hit.id, name: hit.name } : null;
    writeCache(SYM, value);
    if (value && !COIN_MAP[SYM]) {
      COIN_MAP[SYM] = { sym: SYM, id: value.id, name: value.name || SYM, color: '#888', bg: '#1a1a22', _resolved: true };
    }
    return value;
  })();
  inflight.set(SYM, p);
  try { return await p; } finally { inflight.delete(SYM); }
}

/**
 * Bulk-Resolve mit beschränkter Parallelität. Gibt zurück, sobald alle
 * Lookups abgeschlossen sind. Side-Effect: COIN_MAP enthält alle neuen IDs.
 */
export async function resolveCoins(syms, { concurrency = 3 } = {}) {
  const queue = [...new Set((syms || []).filter(Boolean).map(s => String(s).toUpperCase()))]
    .filter(s => !COIN_MAP[s]?.id); // nur unbekannte
  if (!queue.length) return {};
  const out = {};
  async function worker() {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const s = queue.shift();
      if (!s) break;
      const r = await resolveCoin(s);
      out[s] = r ? r.id : null;
    }
  }
  const n = Math.min(concurrency, queue.length);
  await Promise.all(Array.from({ length: n }, worker));
  return out;
}
